import { isoToNaiveInTimezone } from './dateTimeUtils';
import { sessionCalendarKind, sessionKindLabel } from './sessionKindDisplay';

/** Civil HH:mm:ss for timetable slots (no timezone conversion).
 * Tile contract is covered by src/utils/__tests__/calendarEvents.test.js — keep in sync.
 */
export function normalizeTimeHms(value) {
  const s = String(value || '').trim();
  if (!s) return null;
  const match = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return null;
  return `${match[1].padStart(2, '0')}:${match[2]}:${match[3] || '00'}`;
}

/**
 * Primary teacher's own class that was given to another teacher.
 * Cancelled, holiday, and merged tiles keep those labels.
 */
export function outgoingSubstituteLabel(row) {
  if (row?.cover_source !== 'substituted_away') return null;
  const kind = String(row?.session_kind || 'regular');
  if (kind === 'cancelled' || kind === 'holiday' || kind === 'merged') return null;
  const name = String(row?.substitute_teacher_name || '').trim();
  return name ? `sub: ${name}` : 'substituted';
}

/**
 * Batch / cover / makeup tile. Civil date+time — do not convert across timezones.
 */
export function sessionToCalendarEvent(row) {
  const date = row?.session_date ? String(row.session_date).slice(0, 10) : '';
  const start = normalizeTimeHms(row?.start_time);
  const end = normalizeTimeHms(row?.end_time);
  if (!date || !start || !end) return null;

  const kind = row?.session_kind || 'regular';
  const hasRealSession = !!row?.id;
  const calId = hasRealSession
    ? String(row.id)
    : `sch-${row.batch_id}-${date}-${start}-${end}`;
  // A cancelled, holiday, or merged class keeps that label for the cover teacher too.
  const blocksRoleLabel = kind === 'cancelled' || kind === 'holiday' || kind === 'merged';
  const isSecondary = !blocksRoleLabel && row?.cover_source === 'secondary';
  const handedOffLabel = blocksRoleLabel ? null : outgoingSubstituteLabel(row);
  const coverLabel = blocksRoleLabel
    ? null
    : isSecondary
      ? 'secondary'
      : row?.cover_source === 'session_cover'
        ? 'cover class'
        : row?.cover_source === 'leave_substitute'
          ? 'substitute'
          : handedOffLabel;
  const joinedNames = String(row?.merged_from_batch_names || '').trim();
  const isIncomingMerge = !!(row?.incoming_merge || joinedNames);
  const isMergedSource = kind === 'merged';
  const sentToName = String(row?.merged_into_batch_name || '').trim();
  // The class that does not run says where it went. The class that still runs names who joined.
  const sentToLabel = isMergedSource ? (sentToName ? `into ${sentToName}` : 'merged') : null;
  const joinedLabel = isIncomingMerge ? (joinedNames ? `${joinedNames} joined` : 'joined') : null;
  const makeupOrCancelLabel = sessionKindLabel(row);
  const kindSubtitle = isMergedSource
    ? sentToLabel
    : !hasRealSession || kind === 'scheduled'
      ? 'scheduled'
      : makeupOrCancelLabel && makeupOrCancelLabel !== 'regular'
        ? makeupOrCancelLabel
        : null;
  // A secondary teacher's makeup keeps both facts: the class is a makeup, and this teacher is secondary.
  const baseSubtitle =
    isSecondary && kindSubtitle && kindSubtitle !== 'scheduled'
      ? `${kindSubtitle} · secondary`
      : coverLabel || kindSubtitle;
  const subtitle = joinedLabel
    ? (baseSubtitle ? `${joinedLabel} · ${baseSubtitle}` : joinedLabel)
    : baseSubtitle;
  const sessionKindKey = sessionCalendarKind(row);
  const calendarKind = isSecondary && sessionKindKey === 'makeup'
    ? 'makeup'
    : isSecondary
      ? 'secondary'
      : handedOffLabel
        ? 'substituted'
        : coverLabel
          ? 'cover'
          : isMergedSource
            ? 'merged'
            : kind === 'scheduled'
              ? 'scheduled'
              : sessionKindKey;

  return {
    id: calId,
    title: row.batch_name || 'Batch',
    start: `${date}T${start}`,
    end: `${date}T${end}`,
    extendedProps: {
      session: row,
      batchId: row.batch_id,
      sessionId: hasRealSession ? row.id : null,
      sessionDate: date,
      startTime: start,
      endTime: end,
      kind: calendarKind,
      subtitle,
      coverSource: row?.cover_source || null,
    },
  };
}

/**
 * Demo / trial tile. Uses scheduled_at_iso (UTC instant) so a USA evening trial
 * lands on the India morning when the teacher actually teaches it.
 */
export function demoToCalendarEvent(demo) {
  if (!demo) return null;
  const durMin = Number(demo.duration_minutes) || 30;
  let startIso = demo.scheduled_at_iso ? String(demo.scheduled_at_iso) : '';
  if (!startIso) {
    const date = String(demo.scheduled_date || '').slice(0, 10);
    const time = normalizeTimeHms(demo.scheduled_time);
    if (!date || !time) return null;
    startIso = `${date}T${time}`;
  }
  const startMs = Date.parse(startIso);
  const endIso = Number.isNaN(startMs) ? startIso : new Date(startMs + durMin * 60_000).toISOString();
  const label = demo.name || (demo.type ? String(demo.type) : 'Demo');
  return {
    id: `demo-${demo.id || `${demo.scheduled_date}-${demo.scheduled_time}`}`,
    title: label,
    start: startIso,
    end: Number.isNaN(startMs) ? startIso : endIso,
    extendedProps: {
      kind: 'demo',
      subtitle: demo.type ? String(demo.type).replace(/_/g, ' ') : 'demo',
      demoId: demo.id ? String(demo.id) : null,
    },
  };
}

/**
 * Plot a demo on a teacher calendar as naive wall-clock in that teacher's timezone.
 * Does not change demoToCalendarEvent's ISO contract (tests / other callers).
 */
export function demoToNaiveCalendarEvent(demo, teacherTimezone) {
  const ev = demoToCalendarEvent(demo);
  if (!ev) return null;
  const startHasOffset = /Z|[+-]\d{2}:\d{2}$/.test(String(ev.start));
  if (!startHasOffset || !teacherTimezone) return ev;
  const startNaive = isoToNaiveInTimezone(ev.start, teacherTimezone);
  const endNaive = isoToNaiveInTimezone(ev.end, teacherTimezone);
  if (!startNaive) return ev;
  return { ...ev, start: startNaive, end: endNaive || startNaive };
}
